import { useEffect } from "react";
import { IoLanguageOutline } from "react-icons/io5";
import {
  getDashboardLanguage,
  setDashboardLanguage,
  translateDashboardText,
} from "i18n/dashboardLanguage";

const translatedAttributes = ["placeholder", "title", "aria-label"];

const translateElement = (element: Element) => {
  if (element.closest("[data-no-translate='true']")) return;

  translatedAttributes.forEach((attribute) => {
    const value = element.getAttribute(attribute);
    if (!value) return;
    const translated = translateDashboardText(value);
    if (translated !== value) element.setAttribute(attribute, translated);
  });
};

const translateTree = (root: Node) => {
  if (root.nodeType === Node.TEXT_NODE) {
    const parent = root.parentElement;
    if (
      !parent ||
      parent.closest("[data-no-translate='true']") ||
      ["SCRIPT", "STYLE", "TEXTAREA"].includes(parent.tagName)
    ) {
      return;
    }
    const value = root.textContent || "";
    const translated = translateDashboardText(value);
    if (translated !== value) root.textContent = translated;
    return;
  }

  if (root.nodeType !== Node.ELEMENT_NODE) return;
  const element = root as Element;
  translateElement(element);
  element.querySelectorAll("*").forEach(translateElement);

  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    const nextNode = walker.nextNode();
    translateTree(node);
    node = nextNode;
  }
};

export function DashboardTranslationBridge() {
  const language = getDashboardLanguage();

  useEffect(() => {
    document.documentElement.lang = language === "am" ? "am" : "en";
    if (language !== "am") return undefined;

    translateTree(document.body);
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "characterData") {
          translateTree(mutation.target);
          return;
        }
        if (mutation.type === "attributes") {
          translateElement(mutation.target as Element);
          return;
        }
        mutation.addedNodes.forEach(translateTree);
      });
    });
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: translatedAttributes,
    });
    return () => observer.disconnect();
  }, [language]);

  return null;
}

export function DashboardLanguageButton({ className = "" }: { className?: string }) {
  const language = getDashboardLanguage();
  const nextLanguage = language === "en" ? "am" : "en";
  const label = language === "en" ? "አማርኛ" : "English";

  const switchLanguage = () => {
    setDashboardLanguage(nextLanguage);
    window.location.reload();
  };

  return (
    <button
      type="button"
      className={className}
      onClick={switchLanguage}
      aria-label={language === "en" ? "Switch to Amharic" : "Switch to English"}
      data-no-translate="true"
    >
      <IoLanguageOutline size={18} />
      <span>{label}</span>
    </button>
  );
}
